import React, { useMemo, useState } from "react";
import styled, { css } from "styled-components";
import AlertsModals from "security-app/components/SecurityEvents/Alerts/AlertsModals";
import { ModalProvider, useModalContext } from "common/contexts";
import { Spinner, IconButton } from "components/common";
import EventActions from "security-app/components/SecurityEvents/ReplaySearch/EventActions";
import { useGetSecurityEvent } from "security-app/hooks/useSecurityEventsAPI";
import { SegmentedControl, Button, Label } from "components/bootstrap";
import EventDetailsTable from "components/events/events/EventDetailsTable";
import RemediationSteps from "components/events/ReplaySearchSidebar/RemediationSteps";
import columnRenderers from "security-app/components/SecurityEvents/Alerts/ColumnRenderers";
import { securityEventDetailsAttributes } from "security-app/components/SecurityEvents/Alerts/Constants";
import useEventsAdditionalData from "components/events/ReplaySearchSidebar/hooks/useEventsAdditionalData";
import useCurrentUser from "hooks/useCurrentUser";
import { isPermitted } from "util/PermissionsMixin";
import EventNotes from "security-app/components/SecurityEvents/ReplaySearch/EventNotes";
import useOwnerAction from "security-app/components/SecurityEvents/Alerts/hooks/useOwnerAction";
import SetStatusControl from "security-app/components/SecurityEvents/Alerts/EventDetails/SetStatusControl";
import AssocAssetsWidget from "security-app/components/Assets/plugin/AssocAssetsWidget";
import EventDefinitionInfoTable from "components/event-definitions/replay-search/EventDefinitionInfoTable";
import ExpandableSection from "components/events/ReplaySearchSidebar/ExpandableSection";
import ReplaySearchContext from "components/event-definitions/replay-search/ReplaySearchContext";
import useFeature from "hooks/useFeature";
import { eventTypeAttribute } from "components/events/events/ColumnRenderers";
import { WordBreakRenderer } from "components/events/ReplaySearchSidebar/GeneralEventSideBar";
const sidebarAttributesList = [
  ...securityEventDetailsAttributes.filter(
    (attr) => attr.id !== "associated_assets"
  ),
  {
    id: "owner",
    title: "Owner",
    type: "STRING"
  }
];
const legacyAttributesList = [
  {
    id: "timestamp",
    title: "Timestamp",
    type: "DATE"
  },
  {
    id: "alert",
    title: "Type"
  },
  {
    id: "message",
    type: "STRING",
    title: "Message"
  },
  {
    id: "status",
    type: "STRING",
    title: "Status"
  },
  {
    id: "owner",
    type: "STRING",
    title: "Owner"
  }
];
const legacyAttributesRenderers = {
  alert: eventTypeAttribute,
  message: { renderCell: WordBreakRenderer }
};
const CurrentOwner = styled.div`
  display: flex;
  align-items: center;
`;
const OwnerAction = ({ securityEvent }) => {
  const { onRemoveOwner, selectOwner, currentOwnerName } = useOwnerAction(securityEvent);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, !!currentOwnerName && /* @__PURE__ */ React.createElement(CurrentOwner, null, /* @__PURE__ */ React.createElement(Label, { bsSize: "xs" }, currentOwnerName), /* @__PURE__ */ React.createElement(IconButton, { onClick: onRemoveOwner, name: "close", title: "Remove owner" })), !!currentOwnerName || /* @__PURE__ */ React.createElement(Button, { bsSize: "xs", onClick: selectOwner }, "Add owner"));
};
const StatusAction = ({
  securityEvent,
  canManageSecurityEvents
}) => {
  const { setModal, setEntity } = useModalContext();
  const onStatusChange = (newStatus) => {
    if (newStatus !== securityEvent.status) {
      setEntity({
        securityEvent,
        newStatus
      });
      setModal("CONFIRM-STATUS-CHANGE");
    }
  };
  return /* @__PURE__ */ React.createElement(
    SetStatusControl,
    {
      onStatusChange,
      value: securityEvent.status,
      disabled: !canManageSecurityEvents,
      label: null,
      width: null,
      size: "small"
    }
  );
};
const Container = styled.div(
  ({ theme }) => css`
    display: flex;
    gap: ${theme.spacings.sm};
    flex-direction: column;
  `
);
const Section = styled.div(
  ({ theme }) => css`
    display: flex;
    gap: ${theme.spacings.xs};
    flex-direction: column;
  `
);
const Tab = styled.div(
  ({ theme }) => css`
    padding: ${theme.spacings.xs} 0;
  `
);
const segments = [
  { value: "overview", label: "Overview" },
  { value: "procedures", label: "Procedures" },
  { value: "notes", label: "Notes" }
];
const EventDetailsSection = ({
  securityEvent,
  meta,
  canManageSecurityEvents,
  isRightSidebarEnabled
}) => {
  const { event: generalEvent, ...rest } = securityEvent;
  const eventEntity = { ...rest, ...generalEvent };
  const attributesRenderers = {
    ...isRightSidebarEnabled ? columnRenderers.attributes : legacyAttributesRenderers,
    owner: {
      renderCell: () => /* @__PURE__ */ React.createElement(OwnerAction, { securityEvent })
    },
    status: {
      renderCell: () => /* @__PURE__ */ React.createElement(StatusAction, { securityEvent, canManageSecurityEvents })
    }
  };
  return /* @__PURE__ */ React.createElement(ExpandableSection, { title: "Event Details" }, /* @__PURE__ */ React.createElement(
    EventDetailsTable,
    {
      event: isRightSidebarEnabled ? eventEntity : securityEvent == null ? void 0 : securityEvent.event,
      meta,
      attributesList: isRightSidebarEnabled ? sidebarAttributesList : legacyAttributesList,
      attributesRenderers
    }
  ), canManageSecurityEvents && /* @__PURE__ */ React.createElement(Section, null, /* @__PURE__ */ React.createElement(EventActions, { securityEvent })));
};
const EventReplaySideBarDetails = ({ alertId, definitionId }) => {
  var _a, _b, _c, _d;
  const { permissions } = useCurrentUser();
  const [selectedSegment, setSelectedSegment] = useState("overview");
  const isRightSidebarEnabled = useFeature("replay_search_right_sidebar");
  const canManageSecurityEvents = useMemo(() => isPermitted(permissions, "security_event:edit"), [permissions]);
  const { securityEvent, loadingSecurityEvents } = useGetSecurityEvent(alertId);
  const resolvedDefinitionId = definitionId != null ? definitionId : (_a = securityEvent == null ? void 0 : securityEvent.event) == null ? void 0 : _a.event_definition_id;
  const { meta, eventDefinitionEventProcedureId, isLoadingEventDefinition } = useEventsAdditionalData({
    eventData: securityEvent == null ? void 0 : securityEvent.event,
    definitionId: resolvedDefinitionId
  });
  const replaySearchContext = useMemo(
    () => {
      var _a2;
      return {
        alertId,
        definitionId: resolvedDefinitionId,
        type: ((_a2 = securityEvent == null ? void 0 : securityEvent.event) == null ? void 0 : _a2.alert) ? "alert" : "event"
      };
    },
    [alertId, resolvedDefinitionId, (_b = securityEvent == null ? void 0 : securityEvent.event) == null ? void 0 : _b.alert]
  );
  if (alertId && (loadingSecurityEvents || !securityEvent) || isLoadingEventDefinition) return /* @__PURE__ */ React.createElement(Spinner, null);
  return /* @__PURE__ */ React.createElement(ModalProvider, null, /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(SegmentedControl, { data: segments, value: selectedSegment, onChange: setSelectedSegment }), selectedSegment === "overview" && /* @__PURE__ */ React.createElement(Tab, null, /* @__PURE__ */ React.createElement(Container, null, securityEvent && /* @__PURE__ */ React.createElement(
    EventDetailsSection,
    {
      securityEvent,
      meta,
      canManageSecurityEvents,
      isRightSidebarEnabled
    }
  ), /* @__PURE__ */ React.createElement(ReplaySearchContext.Provider, { value: replaySearchContext }, /* @__PURE__ */ React.createElement(ExpandableSection, { title: "Event Definition Details" }, /* @__PURE__ */ React.createElement(EventDefinitionInfoTable, null))), securityEvent && /* @__PURE__ */ React.createElement(ExpandableSection, { title: "Associated Assets" }, ((_d = (_c = securityEvent.event) == null ? void 0 : _c.associated_assets) == null ? void 0 : _d.length) ? /* @__PURE__ */ React.createElement(AssocAssetsWidget, { assetIds: securityEvent.event.associated_assets, direction: "col", label: null }) : /* @__PURE__ */ React.createElement("i", null, "This Event does not have any Associated Assets")))), selectedSegment === "procedures" && /* @__PURE__ */ React.createElement(Tab, null, /* @__PURE__ */ React.createElement(ExpandableSection, { title: "Event Definition Details" }, securityEvent ? /* @__PURE__ */ React.createElement(
    RemediationSteps,
    {
      event: securityEvent == null ? void 0 : securityEvent.event,
      meta,
      eventDefinitionEventProcedureId
    }
  ) : /* @__PURE__ */ React.createElement("em", null, "No event data available"))), selectedSegment === "notes" && /* @__PURE__ */ React.createElement(Tab, null, /* @__PURE__ */ React.createElement(ExpandableSection, { title: "Notes" }, securityEvent ? /* @__PURE__ */ React.createElement(EventNotes, { editable: canManageSecurityEvents, securityEvent }) : /* @__PURE__ */ React.createElement("em", null, "No event data available")))), /* @__PURE__ */ React.createElement(AlertsModals, null));
};
export default EventReplaySideBarDetails;
